import { Component } from 'react';
import { isFunction } from 'util';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { withApi } from './withApi';

import { notify } from '~/services/app/actions';

const withContainer = (WrappedComponent, apiCalls = {}, mapStateToPropsIn = () => ({}), mapDispatchToPropsIn = {}) => {
    const mapStateToProps = state => {
		const additionalStateToProps = mapStateToPropsIn(state);

        return {
            router: state.router,
            app: state.app,
            ...additionalStateToProps
        };
	};

    const mapDispatchToProps = {
        notify,
        ...mapDispatchToPropsIn
    };

    class Container extends Component {
		state = {
			mode: null
		}

        /**
         *	@param { array } promises - An array of promises to call
         *	@param { object } config - A configurable with objects used to display messages, perform actions post asynchronous calls, etc.
         *		`Currently used/possible properties (likely more to come):`
         *			@property { function | string } successRouteTo - A route to navigate to after resolved Promise(s)
         *			@property { function } successAction - A Redux action to dispatch after resolved Promise(s) @example successAction: reloadAccounts
         *
         *	@return { Promise } Returns a Promise that allows us to chain off of `.then()` or `.catch()` if at all needed for custom logic.
         *			For most use-cases, we generally handle all `.catch()` logic within this function and won't need to write your own.
         *			It will be common however to write your own logic in a `.then()` block for setting state after successful calls.
         *
         *	@example
         *
         *   this.props.handlePromises([
         *		this.props.api.saveDeposit(null, data),
         *		...insert multiple API calls here...
         *	]).then(responses => {
         *		...perform post asynchronous actions here...
         *	}
         */
        handlePromises = (promises = [], config = {}) => {
            return Promise.all(Array.isArray(promises) ? promises : [promises])
                .then(response => {
                    return new Promise(resolve => {
                        /*
							Dispatches a redux action after a successful call
							(e.g. we need to navigate to another page and reload data for the screen to properly render)
						*/
                        if (config.successAction) {
                            config.successAction();
                        }

                        // Navigates to route after a resolved call if the `successRouteTo` property exists
                        if (this.props.route) {
                            if (config.successRouteTo) {
								let url = config.successRouteTo;

                                if (isFunction(config.successRouteTo)) {
                                    url = config.successRouteTo(response);
								}

                                this.props.route.history.push(url);
                            }
						}

						if (response.length < 2) {
							resolve(response[0]);
						} else {
							resolve(response);
						}
                    });
                })
                .catch(error => {
                    return new Promise((__, reject) => {
                        if (error.response) {
							const err = error.response.data ? error.response.data : error.response;

                            this.setState({
                                error: err
							});
							reject(err);
                        }

                        reject(error);
                    });
                });
		};

		setMode = mode => {
			this.setState({
				mode
			});
		}

		clearMode = () => {
			this.setState({
				mode: null
			});
		}

        render() {
            return <WrappedComponent {...this.props} handlePromises={this.handlePromises} setMode={this.setMode} clearMode={this.clearMode} mode={this.state.mode} />;
        }
    }

	Container.displayName = `WithContainer(${WrappedComponent.name})`;

    return withApi(
        withRouter(
            connect(
                mapStateToProps,
                mapDispatchToProps
            )(Container)
        ),
        apiCalls
    );
};

export default withContainer;
